import React, { type ReactElement, type ReactNode } from 'react'
import classNames from 'classnames'

// 🔹 Props for the main card
interface CardProps {
	header?: boolean
	key?: string
	title?: string
	classes?: string
	titleClasses?: string
	description?: string
	children: ReactNode
	badge?: ReactNode

	// 🔵 ADDED — allow actions to render below title
	actionsBelow?: boolean
}

interface CardActionsProps {
	children: ReactNode
}

interface CardFooterProps {
	title?: string
	children: ReactNode
}

interface CardErrorsProps {
	children: ReactNode
}

// 🔹 Main component
const Card: React.FC<CardProps> & {
	Actions: React.FC<CardActionsProps>
	Footer: React.FC<CardFooterProps>
	Errors: React.FC<CardErrorsProps>
} = ({
			 header = true,
			 key,
			 title,
			 classes,
			 titleClasses,
			 description,
			 children,
			 badge,
			 actionsBelow = false, // 🔵 ADDED
		 }) => {
	const childrenArray = React.Children.toArray(children)

	const actions = childrenArray.find(child => React.isValidElement(child) && child.type === Card.Actions) as ReactElement<CardActionsProps> | undefined

	const footer = childrenArray.find(child => React.isValidElement(child) && child.type === Card.Footer) as ReactElement<CardFooterProps> | undefined

	const errors = childrenArray.find(child => React.isValidElement(child) && child.type === Card.Errors) as ReactElement<CardErrorsProps> | undefined

	const body = childrenArray.filter(child => !(React.isValidElement(child) && (child.type === Card.Actions || child.type === Card.Footer || child.type === Card.Errors)))

	return (
		<div key={key} className={classNames(classes ? classes : 'surface-panel w-full bg-white', 'rounded-lg p-4 lg:p-7')}>
			{/* Header */}
			{header && (
				<div className="mb-4 border-b border-gray-200 pb-3">
					<div className="flex items-center justify-between gap-3">
						<div className="flex items-center gap-2">
							<div className={classNames('font-semibold text-gray-800', titleClasses ? titleClasses : 'text-xl')}>{title}</div>

							{badge && <div className="ml-2 flex-shrink-0">{badge}</div>}
						</div>

						{/* 🟠 MODIFIED — push actions fully right using ml-auto */}
						{!actionsBelow && actions && <div className="ml-auto flex flex-wrap justify-end gap-2">{actions.props.children}</div>}
					</div>

					{description && <div className="mt-3 text-sm text-gray-600">{description}</div>}

					{errors && <div className="mt-4 mb-2 w-full text-center text-sm text-red-500">{errors.props.children}</div>}

					{/* 🔵 ADDED — actions below title & pushed right */}
					{actionsBelow && actions && <div className="ml-auto mt-4 flex flex-wrap justify-end gap-3">{actions.props.children}</div>}
				</div>
			)}
			{/* Body */}
			<div className="text-base leading-relaxed text-gray-700 sm:text-[1rem]">{body}</div>

			{/* Footer */}
			{footer && (
				<div className="mt-6 border-t border-gray-200 pt-5 text-sm text-gray-600">
					{footer.props.title && <div className="mb-3 text-base font-semibold text-gray-800">{footer.props.title}</div>}
					{footer.props.children}
				</div>
			)}
		</div>
	)
}

// 🔹 Subcomponents
Card.Actions = ({ children }: CardActionsProps) => <>{children}</>
Card.Footer = ({ children }: CardFooterProps) => <>{children}</>
Card.Errors = ({ children }: CardErrorsProps) => <>{children}</>

export default Card
